import React, { useEffect, useRef, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import ImageFileContext from '../../modules/contexts/ImageFile';
import ThemeStyle from '../../lib/style/ThemeStyle';
import { Flex } from '../../components/common/atom/Layout';
import Icon from '../../components/common/atom/Icon';
import { Text } from '../../components/common/atom/Text';

export const IMAGE_SIZE_LIMIT = 20 * 1048576; // 20MB
export const ULTRASOUND = 'ULTRASOUND';
export const REVIEW = 'REVIEW';

const ImageUpload = ({ imageType, multipleOption, limitedNum, preloadImages = [], indelible = false }) => {
  const { state, actions } = useContext(ImageFileContext);
  const imageFileRef = useRef(null);
  let imageList = [];
  let previewList = [];

  useEffect(() => {
    switch (imageType) {
      case ULTRASOUND:
        imageList = state.ultraList;
        previewList = state.ultraPreviewList;
        break;
      case REVIEW:
        imageList = state.reviewList;
        previewList = state.reviewPreviewList;
        break;
      default:
        break;
    }
  }, [state]);

  useEffect(() => {
    if (preloadImages && preloadImages.length > 0) {
      imageList = [];
      previewList = [];
      actions.setUltraList([]);
      actions.setUltraPreviewList([]);
      selectImages(preloadImages);
    }
  }, []);

  const resizeImage = (image, fileName, fileType) => {
    let file;
    const canvas = document.createElement('canvas');
    canvas.width =
      image.width > image.height
        ? STANDARD_SIZE
        : STANDARD_SIZE * (image.width / image.height);
    canvas.height =
      image.width > image.height
        ? STANDARD_SIZE * (image.height / image.width)
        : STANDARD_SIZE;

    const context = canvas.getContext('2d');
    context.drawImage(
      image,
      0,
      0,
      image.width,
      image.height,
      0,
      0,
      canvas.width,
      canvas.height,
    );

    canvas.toBlob((blob) => {
      file = new File([blob], `resize_${fileName}`, {
        type: fileType,
      });
      actions.setUltraList((imageList) => imageList.concat(file));
    }, fileType);
  };

  const imageHandler = useCallback(
    (e) => {
      e.preventDefault();
      if (e.target.files[0] === undefined) return;
      selectImages([...e.target.files]);
    },
    [state],
  );

  const selectImages = useCallback(
    async (fileList) => {
      if (typeof FileReader !== undefined) {
        try {
          // 초음파 용량 제한
          if (imageType === ULTRASOUND && fileList[0].size > IMAGE_SIZE_LIMIT) {
            alert('이미지는 20MB 이하 크기로 올려주세요.');
            imageFileRef.current.value = '';
            return;
          }

          // 업로드 사진 개수 제한
          if (imageList.length + fileList.length > limitedNum) {
            return alert(`이미지는 최대 ${limitedNum}장까지 올릴 수 있습니다`);
          }

          for (let i = 0; i < fileList.length; i++) {
            let file = fileList[i];

            // file이 File 인스턴스가 아닌 경우, URL로 간주 및 다운로드
            if (!(file instanceof File)) {
              const response = await fetch(new URL(file));
              if (!response.ok) {
                alert('이미지 URL 로딩에 실패 했습니다. 다시 시도해 주세요.');
                break;
              }
              const blob = await response.blob();
              const filename = new URL(file).pathname.split('/').pop();
              file = new File([blob], filename, { type: blob.type });
            }

            const reader = new FileReader();
            reader.readAsDataURL(file);

            reader.onload = async () => {
              switch (imageType) {
                case ULTRASOUND:
                  const image = document.createElement('img');
                  image.src = reader.result;
                  try {
                    await image.decode();
                  } catch {
                    alert('이미지 로딩에 실패 했습니다. 다시 시도해 주세요.');
                    break;
                  }
                  image.width > STANDARD_SIZE || image.height > STANDARD_SIZE
                    ? resizeImage(image, file.name, file.type)
                    : actions.setUltraList((imageList) =>
                        imageList.concat(file),
                      );
                  actions.setUltraPreviewList((previewList) =>
                    previewList.concat(reader.result),
                  );
                  break;
                case REVIEW:
                  actions.setReviewList((imageList) => imageList.concat(file));
                  actions.setReviewPreviewList((previewList) =>
                    previewList.concat(reader.result),
                  );
                  break;
                default:
                  break;
              }
            };

            reader.onerror = () => {
              alert(
                `파일 읽기에 실패했습니다. 다시 시도해주세요.\n
                error code : ${reader.error.code}`,
              );
            };

            imageFileRef.current.value = '';
          }
        } catch (error) {
          alert(
            `파일 업로드에 실패했습니다.\n
            새로고침 또는 크롬, 엣지, 사파리 브라우저로 재접속 후 이용 부탁드립니다.\n
            error: ${error}`,
          );
        }
      } else {
        alert(
          '현재 브라우저에선 파일 업로드를 지원하지 않습니다. 크롬, 엣지, 사파리 브라우저를 이용해주세요.',
        );
      }
    },
    [state],
  );

  const doShowuploadImagePreview = useCallback(() => {
    switch (imageType) {
      case ULTRASOUND:
        return uploadImagePreviewMap(state.ultraPreviewList);
      case REVIEW:
        return uploadImagePreviewMap(state.reviewPreviewList);
      default:
        return;
    }
  }, [previewList]);

  const uploadImagePreviewMap = (preview) => {
    return preview.map((imgUrl, index) => (
      <ImageContainer key={index}>
        {!indelible && 
          <DeleteIcon
            onClick={(e) => {
              e.stopPropagation();
              deleteImageHandler(imageList, previewList, index);
            }}
          >
            X
          </DeleteIcon>
        }
        <Image src={imgUrl} alt="Upload Image" />
      </ImageContainer>
    ));
  };

  const deleteImageHandler = useCallback(
    (file, preview, index) => {
      const deleteFile = file
        .slice(0, index)
        .concat(file.slice(index + 1, file.length));
      const deletePreview = preview
        .slice(0, index)
        .concat(preview.slice(index + 1, preview.length));

      switch (imageType) {
        case ULTRASOUND:
          actions.setUltraList(deleteFile);
          actions.setUltraPreviewList(deletePreview);
          break;
        case REVIEW:
          actions.setReviewList(deleteFile);
          actions.setReviewPreviewList(deletePreview);
          break;
        default:
          return;
      }
    },
    [imageList, previewList],
  );

  const doShowUploadButton = useCallback(() => {
    switch (imageType) {
      case ULTRASOUND:
        return uploadButton(state.ultraList);
      case REVIEW:
        return uploadButton(state.reviewList);
      default:
        return;
    }
  }, [imageList]);

  const uploadButton = (list) => {
    return (
      list.length < limitedNum && (
        <Flex
          column
          width="100%"
          height="8rem"
          style={{
            borderRadius: '6px',
            border: `dashed 1px ${ThemeStyle.color.pinkishGrey}`,
            background: ThemeStyle.background.grey,
          }}
          onClick={() => imageFileRef.current.click()}
        >
          <Icon iconName="photo_camera" />
          <Text size={14} weight="medium" style={{ marginTop: '0.625rem' }}>
            초음파 사진 첨부하기
          </Text>
        </Flex>
      )
    );
  };

  return (
    <>
      <input
        style={{ display: 'none' }}
        type="file"
        accept="image/jpg,image/png,image/jpeg"
        onChange={imageHandler}
        multiple={multipleOption}
        ref={imageFileRef}
      />
      {doShowuploadImagePreview()}
      {doShowUploadButton()}
    </>
  );
};

export default React.memo(ImageUpload);

ImageUpload.propTypes = {
  imageType: PropTypes.string.isRequired,
  multipleOption: PropTypes.bool.isRequired,
  limitedNum: PropTypes.number.isRequired,
};

const STANDARD_SIZE = 1024;

const ImageContainer = styled(Flex)`
  position: relative;
  width: 100%;
  border-radius: 6px;
  border: dashed 1px ${ThemeStyle.color.pinkishGrey};
  background: ${(props) => props.theme.background.grey};
  height: 8rem;
`;

const DeleteIcon = styled(Flex)`
  position: absolute;
  right: 0;
  top: 0;
  width: 1.25rem;
  height: 1.25rem;
  background-color:${(props) => props.theme.color.pinkishGrey};
  font-size: 0.875rem;
  color: ${(props) => props.theme.color.white}
  cursor: pointer;
`;
const Image = styled.img`
  width: 100%;
  height: 90%;
  object-fit: contain;
`;
